/**
 * Spec 129 — typed client for destructive brand operations.
 *
 * Hard-delete + cascade purge of a brand and ALL of its collected data:
 *   DELETE /api/v1/tenants/:tenantId/brands/:brandId/purge
 *     body { confirm_name: string }
 *
 * The server requires `confirm_name` to match the brand's exact name; a
 * mismatch returns 400 `{ error: "confirm_name does not match" }`. On success
 * it returns the per-table delete counts so the UI can report the blast radius.
 *
 * Types intentionally duplicated from the server — the Vite client and the
 * Fastify server share no module graph.
 */
import { apiFetch } from '../lib/api.js';

export interface BrandPurgeResult {
  brand_id: string;
  purged: true;
  /** Per-table delete counts, keyed by table name. */
  deleted: Record<string, number>;
  /** Total rows removed across all tables. */
  total: number;
}

interface PurgeApiError {
  error: string;
  details?: string[];
}

/**
 * Hard-delete a brand and every record that references it. Throws an Error
 * carrying the server message on any non-2xx (e.g. a 400 name mismatch), so
 * callers can surface it in a toast.
 */
export async function purgeBrand(
  tenantId: string,
  brandId: string,
  confirmName: string,
): Promise<BrandPurgeResult> {
  const res = await apiFetch(
    `/api/v1/tenants/${tenantId}/brands/${brandId}/purge`,
    {
      method: 'DELETE',
      body: JSON.stringify({ confirm_name: confirmName }),
    },
  );
  if (!res.ok) {
    let message = `Brand purge failed: ${res.status}`;
    try {
      const err = (await res.json()) as PurgeApiError;
      if (err?.error) message = err.error;
    } catch {
      // Non-JSON body — keep the status-based message.
    }
    throw new Error(message);
  }
  const json = (await res.json()) as { data: BrandPurgeResult };
  return json.data;
}
